import React, {useState} from 'react';
import {Checkbox, FlexColumn, Label, Modal, Text, TextField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import Util from '../../shared/util/util';
import UpdateFirmPortalSettingsMutation from '../../../mutations/update_firm_portal_settings_mutation';
import {showChangesSavedToast} from '../../shared/components/toasts/toast';

/**
 * Global Settings → Client Portal tab. Firm-wide portal settings: subdomain,
 * default feature-flag values for new client orgs, notification email infra
 * (sender domain + reply-to), firm-wide phase-status flag. Branding is at the
 * client org level only. Reads viewer.firmPortalSettings and saves through the
 * portal proxy (no forecast_db write).
 */

/**
 * Error codes the portal proxy raises for a subdomain the user can fix, mapped to
 * the message shown inline under the field. Anything else falls through to the
 * shared mutation error modal — these two are the only rejections the person
 * typing can do something about.
 */
const SUBDOMAIN_ERROR_MESSAGES: Record<string, {id: string; defaultMessage: string}> = {
	DUPLICATE_PORTAL_SUBDOMAIN: {
		id: 'client_portal.subdomain_taken',
		defaultMessage: 'That subdomain is already taken. Choose a different one.',
	},
	INVALID_PORTAL_SUBDOMAIN: {
		id: 'client_portal.subdomain_invalid',
		defaultMessage: 'Enter 3–63 characters using lowercase letters, numbers, and hyphens.',
	},
};
type FirmPortalSettings = {
	portalSubdomain: string | null;
	portalCustomDomain: string | null;
	portalSenderDomain: string | null;
	portalReplyTo: string | null;
	defaultShowInvoices: boolean;
	defaultAllowFileUpload: boolean;
	phaseStatusEnabled: boolean;
};

// Every field is nullable in the schema, including everImported — the whole object is
// null for a viewer the portal-config gate rejects, and that is indistinguishable here
// from a firm that has never synced. Both read as "not imported", which is the honest
// answer when nothing can be proven.
type PortalDataImportStatus = {
	everImported: boolean | null;
	lastSuccessAt: string | null;
	outcome: string | null;
	reconcileSkipped: number | null;
};

type ClientPortalSettingsProps = {
	viewer: {
		firmPortalSettings: FirmPortalSettings | null;
		portalDataImportStatus: PortalDataImportStatus | null;
	};
};

export const ClientPortalSettings = ({viewer}: ClientPortalSettingsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const s = viewer?.firmPortalSettings ?? null;
	const importStatus = viewer?.portalDataImportStatus ?? null;

	const [subdomain, setSubdomain] = useState<string>(s?.portalSubdomain || '');
	const [subdomainError, setSubdomainError] = useState<string | null>(null);
	const [senderDomain, setSenderDomain] = useState<string>(s?.portalSenderDomain || '');
	const [replyTo, setReplyTo] = useState<string>(s?.portalReplyTo || '');
	const [defaultShowInvoices, setDefaultShowInvoices] = useState<boolean>(!!s?.defaultShowInvoices);
	const [defaultAllowFileUpload, setDefaultAllowFileUpload] = useState<boolean>(!!s?.defaultAllowFileUpload);
	const [phaseStatusEnabled, setPhaseStatusEnabled] = useState<boolean>(
		s?.phaseStatusEnabled == null ? true : !!s.phaseStatusEnabled
	);

	const onSave = () => {
		// Clear the previous verdict up front: leaving a stale "already taken" under a
		// field the user has since edited reads as though the new value was rejected too.
		setSubdomainError(null);
		Util.CommitMutation(
			UpdateFirmPortalSettingsMutation,
			{
				// An emptied field clears the subdomain rather than saving '', which the
				// backend's shape check would reject.
				portalSubdomain: subdomain.trim() || null,
				portalSenderDomain: senderDomain || null,
				portalReplyTo: replyTo || null,
				defaultShowInvoices,
				defaultAllowFileUpload,
				phaseStatusEnabled,
			},
			() => showChangesSavedToast(formatMessage),
			true,
			null,
			(transaction, onFailure) => {
				const error = transaction ? transaction.toString() : '';
				const matched = Object.keys(SUBDOMAIN_ERROR_MESSAGES).find(code => error.includes(code));
				if (matched) {
					setSubdomainError(formatMessage(SUBDOMAIN_ERROR_MESSAGES[matched]));
					return;
				}
				// Not a subdomain problem — hand back to the shared error modal so
				// correlation-id reporting and retry behaviour are preserved.
				onFailure(transaction);
			}
		);
	};

	return (
		<FlexColumn gap={'l'} data-cy="client-portal-settings">
			<Text size="2">
				<FormattedMessage id="client_portal.global_settings_title" defaultMessage="Client Workspace" />
			</Text>

			<TextField
				label={formatMessage({id: 'client_portal.subdomain', defaultMessage: 'Workspace subdomain'})}
				value={subdomain}
				onValueChange={value => {
					setSubdomain(value);
					// Editing is the user's answer to the rejection, so retire it as they type
					// instead of leaving a red field under a value that has not been tried yet.
					setSubdomainError(null);
				}}
				errorText={subdomainError ?? undefined}
				helperText={formatMessage({
					id: 'client_portal.subdomain_helper',
					defaultMessage: 'Lowercase letters, numbers, and hyphens, 3–63 characters.',
				})}
				size="large"
				width="flex"
				data-cy="client-portal-subdomain"
			/>
			<TextField
				label={formatMessage({id: 'client_portal.sender_domain', defaultMessage: 'Reply-to domain (fallback)'})}
				value={senderDomain}
				onValueChange={setSenderDomain}
				helperText={formatMessage({
					id: 'client_portal.sender_domain_helper',
					defaultMessage:
						"Replies go here when no reply-to address is set. The From address always uses Forecast's verified sending domain.",
				})}
				size="large"
				width="flex"
			/>
			<TextField
				label={formatMessage({id: 'client_portal.reply_to', defaultMessage: 'Reply-to address'})}
				value={replyTo}
				onValueChange={setReplyTo}
				size="large"
				width="flex"
			/>

			<Label>
				<FormattedMessage id="client_portal.default_flags" defaultMessage="Defaults for new client orgs" />
			</Label>
			<Checkbox
				checked={defaultShowInvoices}
				onChange={() => setDefaultShowInvoices(p => !p)}
				labelText={formatMessage({id: 'client_portal.flag_show_invoices', defaultMessage: 'Show invoices'})}
			/>
			<Checkbox
				checked={defaultAllowFileUpload}
				onChange={() => setDefaultAllowFileUpload(p => !p)}
				labelText={formatMessage({id: 'client_portal.flag_allow_file_upload', defaultMessage: 'Allow file upload'})}
			/>
			<Checkbox
				checked={phaseStatusEnabled}
				onChange={() => setPhaseStatusEnabled(p => !p)}
				labelText={formatMessage({
					id: 'client_portal.phase_status_enabled',
					defaultMessage: 'Phase status enabled (firm-wide)',
				})}
			/>

			{/*
			  Read-only import health panel. A firm whose PSA data has never imported
			  would otherwise see exactly what a firm with no retainers sees — nothing —
			  and its clients would be told "No retainers are set up for your workspace".
			  Surfacing the never-imported / failing / partially-reconciled states here
			  is the firm-side half of telling those cases apart; sync state is reported,
			  never authored, so this must not feed onSave.
			*/}
			<Label>
				<FormattedMessage id="client_portal.data_import" defaultMessage="Client data import" />
			</Label>
			{!importStatus || !importStatus.everImported ? (
				<Text size="2">
					<FormattedMessage
						id="client_portal.data_import_never"
						defaultMessage="No client data has been imported yet. Client workspaces will appear empty until the first import succeeds."
					/>
				</Text>
			) : (
				<>
					<Text size="2">
						{importStatus.outcome === 'failed' ? (
							<FormattedMessage
								id="client_portal.data_import_failing"
								defaultMessage="The last import attempt failed. Client workspaces may be showing stale data."
							/>
						) : (
							<FormattedMessage
								id="client_portal.data_import_ok"
								defaultMessage="Client data is importing normally."
							/>
						)}
					</Text>
					{importStatus.lastSuccessAt ? (
						<Text size="2" color="auxiliary">
							<FormattedMessage
								id="client_portal.data_import_last_success"
								defaultMessage="Last successful import: {when}"
								values={{when: new Date(importStatus.lastSuccessAt).toLocaleString()}}
							/>
						</Text>
					) : null}
					{importStatus.reconcileSkipped ? (
						<Text size="2">
							<FormattedMessage
								id="client_portal.data_import_unconverged"
								defaultMessage="{count} project(s) could not be reconciled automatically. Re-publish them from the project's Client portal settings."
								values={{count: importStatus.reconcileSkipped}}
							/>
						</Text>
					) : null}
				</>
			)}

			<Modal.PrimaryFooterButton onPress={onSave}>
				<FormattedMessage id="common.save" defaultMessage="Save" />
			</Modal.PrimaryFooterButton>
		</FlexColumn>
	);
};

export const ClientPortalSettingsQuery = graphql`
	query ClientPortalSettings_Query {
		viewer {
			actualPersonId
			component(name: "client_portal_settings")
			firmPortalSettings {
				portalSubdomain
				portalCustomDomain
				portalSenderDomain
				portalReplyTo
				defaultShowInvoices
				defaultAllowFileUpload
				phaseStatusEnabled
			}
			portalDataImportStatus {
				everImported
				lastSuccessAt
				outcome
				reconcileSkipped
			}
		}
	}
`;

export default ClientPortalSettings;
